import React, { useState, useEffect } from 'react';
import config from '../config/config';
import { DEFAULT_DESIGN, mergeDesign, buildLoginCss } from './login/loginDesign';
import HeroMedia from './login/HeroMedia';

const API_BASE = config.api.baseUrl;

// 2026-09-04 — the design is admin-editable (Setup → עיצוב דף ההתחברות).
// We cache the last-seen design in localStorage so a returning visitor
// gets the configured layout on the FIRST paint instead of watching the
// page rearrange itself when the fetch lands. The network copy still
// wins; this only removes the flash.
const DESIGN_CACHE_KEY = 'login_design_v1';

// A configured URL may be site-relative (/assets/...) or a full https
// link. Only the relative form gets the API origin prepended.
const assetUrl = (u) => (!u ? '' : /^https?:\/\//i.test(u) ? u : `${API_BASE}${u}`);

const readCachedDesign = () => {
  try {
    const raw = localStorage.getItem(DESIGN_CACHE_KEY);
    return raw ? mergeDesign(JSON.parse(raw)) : DEFAULT_DESIGN;
  } catch {
    return DEFAULT_DESIGN;
  }
};

/**
 * LoginPage - Afikim-style login page with company branding
 */
const LoginPage = ({ onLogin }) => {
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [design, setDesign] = useState(readCachedDesign);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pull the admin-configured appearance. Every failure path keeps the
  // current (cached or default) design — the login screen must render
  // even when the flag store is unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/login-design`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setDesign(mergeDesign(data && data.design));
        try {
          localStorage.setItem(DESIGN_CACHE_KEY, JSON.stringify(data.design || {}));
        } catch { /* private mode — the fetch still applied */ }
      } catch (e) {
        console.warn('[LoginPage] design fetch failed, using defaults', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2026-06-24: When App.js detects an expired session (401 on periodic
  // /api/auth/session) it clears tokens, sets this flag, and reloads. We
  // surface it as a notice on the login screen instead of silently
  // returning the user here.
  useEffect(() => {
    if (sessionStorage.getItem('session_expired_notice') === '1') {
      setError('הסשן פג תוקף — נדרשת התחברות מחדש');
      sessionStorage.removeItem('session_expired_notice');
    }
  }, []);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=username, 2=method, 3=code, 4=newPassword
  const [forgotUsername, setForgotUsername] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleForgotInitiate = async () => {
    if (!forgotUsername) { setForgotError('נא להזין שם משתמש'); return; }
    setForgotLoading(true);
    setForgotError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername })
      });
      const data = await res.json();
      if (data.success) {
        setMaskedPhone(data.maskedPhone);
        setForgotStep(2);
      } else {
        setForgotError(data.detail || 'שגיאה');
      }
    } catch (err) { console.error('[ForgotPW] initiate error:', err); setForgotError('שגיאת תקשורת - נסה שוב'); }
    setForgotLoading(false);
  };

  const handleForgotSendOtp = async (method) => {
    setForgotLoading(true);
    setForgotError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername, method })
      });
      const data = await res.json();
      if (data.success) {
        setForgotStep(3);
      } else {
        setForgotError(data.detail || data.error || 'שגיאה בשליחת הקוד');
      }
    } catch (err) { console.error('[ForgotPW] send-otp error:', err); setForgotError('שגיאת תקשורת - נסה שוב'); }
    setForgotLoading(false);
  };

  const handleForgotVerify = async () => {
    if (!otpCode || otpCode.length < 4) { setForgotError('נא להזין קוד אימות'); return; }
    setForgotLoading(true);
    setForgotError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername, code: otpCode })
      });
      const data = await res.json();
      if (data.success) {
        setNewPassword(data.newPassword);
        setForgotStep(4);
      } else {
        setForgotError(data.detail || data.error || 'קוד שגוי');
      }
    } catch (err) { console.error('[ForgotPW] verify error:', err); setForgotError('שגיאת תקשורת - נסה שוב'); }
    setForgotLoading(false);
  };

  const resetForgotFlow = () => {
    setForgotMode(false);
    setForgotStep(1);
    setForgotUsername('');
    setMaskedPhone('');
    setOtpCode('');
    setNewPassword('');
    setForgotError('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('יש להזין שם משתמש וסיסמא');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call real authentication API
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        setError(`שגיאת שרת (${response.status})`);
        return;
      }

      if (response.ok && data.success) {
        const user = {
          Id: data.user.id,
          id: data.user.id,
          username: data.user.username,
          name: data.user.name || data.user.username,
          Name: data.user.name || data.user.username,
          email: data.user.email,
          FirstName: data.user.firstName,
          LastName: data.user.lastName,
          profile: data.user.profile,
          role: data.user.role,
          technical_type: data.user.technical_type,
          isAdmin: data.user.is_admin,
          service_resource_id: data.user.service_resource_id,
          // 2026-05-19: propagate the must-change-password flag.
          // Without this the App.js useEffect can't open the blocking
          // change-password modal because the field was being dropped
          // here when we cherry-picked the login response into our
          // local user object.
          mustChangePassword: data.user.mustChangePassword === true,
          session_token: data.session_token,
          loginTime: new Date().toISOString()
        };

        // Store session token and user info
        if (formData.rememberMe) {
          localStorage.setItem('session_token', data.session_token);
          localStorage.setItem('salesforce_user', JSON.stringify(user));
        } else {
          sessionStorage.setItem('session_token', data.session_token);
          sessionStorage.setItem('salesforce_user', JSON.stringify(user));
        }

        onLogin(user);
      } else {
        setError(data.detail || data.error || 'שם משתמש או סיסמא שגויים');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('שגיאת תקשורת עם השרת - לא ניתן להתחבר');
      } else {
        setError('שגיאת תקשורת עם השרת');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Hero image. Side, size and fit come from the admin design; in
          'fullscreen' layout this same block sits behind the whole page. */}
      {design.heroImageUrl ? (
        <div className="login-hero">
          <HeroMedia design={design} resolveUrl={assetUrl} />
          <div className="hero-overlay"></div>
        </div>
      ) : null}

      {/* Left side - Login form */}
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {/* Logo */}
          <div className="login-logo">
            {logoLoaded && (
              <img
                src={assetUrl(design.logoUrl)}
                alt="Company Logo"
                className="company-logo loaded"
              />
            )}
            <img
              src={assetUrl(design.logoUrl)}
              alt=""
              style={{ display: 'none' }}
              onLoad={() => setLogoLoaded(true)}
            />
            {!logoLoaded && (
              <div className="logo-fallback">
                <span className="logo-icon">☁️</span>
                <span className="logo-text">אפיקים מים</span>
              </div>
            )}
          </div>

          {forgotMode ? (
            <>
              {/* Forgot Password Flow */}
              <div className="login-title">
                <h2>שחזור סיסמה</h2>
                <p>{forgotStep === 1 ? 'הזן את שם המשתמש שלך' : forgotStep === 2 ? 'בחר שיטת אימות' : forgotStep === 3 ? 'הזן קוד אימות' : 'הסיסמה החדשה שלך'}</p>
              </div>

              {forgotError && (
                <div className="login-error">
                  <span className="error-icon">!</span>
                  <span>{forgotError}</span>
                </div>
              )}

              {forgotStep === 1 && (
                <div className="login-form">
                  <div className="form-group">
                    <label>שם משתמש</label>
                    <div className="input-wrapper">
                      <input type="text" value={forgotUsername} onChange={e => { setForgotUsername(e.target.value); setForgotError(''); }} placeholder="user@company.com" dir="ltr" autoFocus />
                    </div>
                  </div>
                  <button type="button" className={`login-button ${forgotLoading ? 'loading' : ''}`} disabled={forgotLoading} onClick={handleForgotInitiate}>
                    {forgotLoading ? <><span className="spinner"></span><span>בודק...</span></> : <span>המשך</span>}
                  </button>
                </div>
              )}

              {forgotStep === 2 && (
                <div className="login-form">
                  <p style={{textAlign:'center', marginBottom:'16px', color:'#555'}}>נשלח קוד אימות למספר: <strong>{maskedPhone}</strong></p>
                  <p style={{textAlign:'center', marginBottom:'20px', color:'#777', fontSize:'14px'}}>בחר שיטת שליחה:</p>
                  <button type="button" className="login-button" style={{marginBottom:'10px', background:'#25D366'}} disabled={forgotLoading} onClick={() => handleForgotSendOtp('whatsapp')}>
                    {forgotLoading ? <><span className="spinner"></span><span>שולח...</span></> : <span>WhatsApp</span>}
                  </button>
                  <button type="button" className="login-button" style={{background:'#0078D4'}} disabled={forgotLoading} onClick={() => handleForgotSendOtp('voice')}>
                    {forgotLoading ? <><span className="spinner"></span><span>מחייג...</span></> : <span>שיחה קולית</span>}
                  </button>
                </div>
              )}

              {forgotStep === 3 && (
                <div className="login-form">
                  <p style={{textAlign:'center', marginBottom:'16px', color:'#555'}}>נשלח קוד אימות למספר: <strong>{maskedPhone}</strong></p>
                  <div className="form-group">
                    <label>קוד אימות (6 ספרות)</label>
                    <div className="input-wrapper">
                      <input type="text" value={otpCode} onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setForgotError(''); }}
                        placeholder="000000" dir="ltr" autoFocus style={{textAlign:'center', fontSize:'24px', letterSpacing:'8px'}} maxLength={6} />
                    </div>
                  </div>
                  <button type="button" className={`login-button ${forgotLoading ? 'loading' : ''}`} disabled={forgotLoading || otpCode.length < 6} onClick={handleForgotVerify}>
                    {forgotLoading ? <><span className="spinner"></span><span>מאמת...</span></> : <span>אמת קוד</span>}
                  </button>
                  <div style={{textAlign:'center', marginTop:'12px'}}>
                    <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); setForgotStep(2); setOtpCode(''); setForgotError(''); }}>שלח קוד שוב</a>
                  </div>
                </div>
              )}

              {forgotStep === 4 && (
                <div className="login-form">
                  <div style={{background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'8px', padding:'20px', textAlign:'center', marginBottom:'16px'}}>
                    <div style={{fontSize:'32px', marginBottom:'8px'}}>&#10003;</div>
                    <h3 style={{margin:'0 0 12px', color:'#166534'}}>הסיסמה אופסה בהצלחה!</h3>
                    <p style={{margin:'0 0 8px', color:'#555'}}>הסיסמה החדשה שלך:</p>
                    <div style={{background:'#fff', border:'1px solid #d1d5db', borderRadius:'6px', padding:'12px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                      <code style={{fontSize:'18px', fontWeight:'bold', letterSpacing:'1px', direction:'ltr'}}>{newPassword}</code>
                      <button type="button" style={{background:'none', border:'none', cursor:'pointer', fontSize:'18px'}} title="העתק"
                        onClick={() => { navigator.clipboard.writeText(newPassword); }}>&#128203;</button>
                    </div>
                    <p style={{margin:'12px 0 0', fontSize:'13px', color:'#888'}}>תתבקש לשנות את הסיסמה בכניסה הראשונה</p>
                  </div>
                  <button type="button" className="login-button" onClick={resetForgotFlow}>
                    <span>חזרה להתחברות</span>
                  </button>
                </div>
              )}

              {forgotStep < 4 && (
                <div style={{textAlign:'center', marginTop:'16px'}}>
                  <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); resetForgotFlow(); }}>חזרה להתחברות</a>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Title */}
              <div className="login-title">
                <h2>{design.title}</h2>
                {design.subtitle ? <p>{design.subtitle}</p> : null}
              </div>

              {/* Error message */}
              {error && (
                <div className="login-error">
                  <span className="error-icon">!</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Login form */}
              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <label htmlFor="username">שם משתמש</label>
                  <div className="input-wrapper">
                    <input
                      id="username"
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleChange('username', e.target.value)}
                      placeholder="user@company.com"
                      autoComplete="username"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">סיסמא</label>
                  <div className="input-wrapper">
                    <input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => handleChange('rememberMe', e.target.checked)}
                    />
                    <span>זכור אותי</span>
                  </label>
                  <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); setForgotMode(true); setForgotStep(1); setForgotError(''); }}>שכחת סיסמא?</a>
                </div>

                <button
                  type="submit"
                  className={`login-button ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>מתחבר...</span>
                    </>
                  ) : (
                    <>
                      <span>התחבר</span>
                      <span className="arrow">←</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Footer */}
          <div className="login-footer">
            <div className="footer-links">
              <a href="#privacy">מדיניות פרטיות</a>
              <span>•</span>
              <a href="#terms">תנאי שימוש</a>
              <span>•</span>
              <a href="#help">עזרה</a>
            </div>
          </div>
        </div>
      </div>

      <style>{buildLoginCss(design)}</style>
    </div>
  );
};


export default LoginPage;
